/**
 * renderDisplay — dispatcher for the generic display renderers.
 *
 * Accepts the raw display frame `{ kind, title?, theme?, payload? }` that the
 * agent forwards verbatim (and that `useDisplayChannel` stores as
 * `DisplayMessage.payload`). It selects a renderer purely by `kind`, wraps it
 * in a theme-styled container (accent/label/icon per `theme`, neutral default),
 * and falls back to JSON for unknown/missing kinds.
 *
 * Theme affects STYLING ONLY — it never changes which renderer is chosen nor
 * the layout that renderer produces (Req 2.7, 2.8).
 */
import type { JSX } from 'react';
import { Card } from './Card';
import { ListTable } from './ListTable';
import { Markdown } from './Markdown';
import { ImageBlock, LinkBlock } from './ImageLink';
import { JsonFallback } from './JsonFallback';
import { getTheme } from './theme';
import type {
  CardPayload,
  DisplayFrame,
  ImagePayload,
  LinkPayload,
  ListTablePayload,
  MarkdownPayload,
} from './types';

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function selectRenderer(frame: DisplayFrame, accent: string): JSX.Element {
  const { kind, title, payload } = frame;
  const obj = asObject(payload);

  switch (kind) {
    case 'card':
      return <Card title={title} payload={obj as CardPayload} accent={accent} />;
    case 'list':
    case 'table':
      return <ListTable title={title} payload={obj as ListTablePayload} accent={accent} />;
    case 'markdown':
      return <Markdown title={title} payload={obj as MarkdownPayload} accent={accent} />;
    case 'image':
      return <ImageBlock title={title} payload={obj as ImagePayload} accent={accent} />;
    case 'link':
      return <LinkBlock title={title} payload={obj as LinkPayload} accent={accent} />;
    default:
      // Unknown or missing kind -> readable JSON fallback (Req 2.5).
      return <JsonFallback title={title} data={payload} accent={accent} />;
  }
}

export function renderDisplay(frame: DisplayFrame): JSX.Element {
  const theme = getTheme(frame.theme);

  return (
    <div
      className="w-full rounded-xl border bg-slate-950/40 p-4"
      style={{ borderColor: theme.accent }}
    >
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400">
        {theme.icon ? <span aria-hidden>{theme.icon}</span> : null}
        <span style={{ color: theme.accent }}>{theme.label}</span>
      </div>
      {selectRenderer(frame, theme.accent)}
    </div>
  );
}

export type { DisplayFrame } from './types';
